import React, { useState, useEffect } from 'react';

interface Subscription {
  id: string;
  status: string;
  cancel_at_period_end: boolean;
  current_period_end: number;
  trial_end: number | null;
  amount: number;
  currency: string;
}

interface Invoice {
  id: string;
  number: string;
  amount_paid: number;
  amount_due?: number;
  currency: string;
  status: string;
  created: number;
  pdf: string | null;
  period_start: number;
  period_end: number;
}

const STATUS_LABELS: Record<string, string> = {
  paid:           'Payée',
  open:           'En attente de paiement',
  uncollectible:  'Impayée',
  void:           'Annulée',
};

const fmt = (ts?: number | null) =>
  ts ? new Date(ts * 1000).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';
const fmtAmount = (amount?: number | null, currency?: string) =>
  typeof amount === 'number'
    ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: (currency || 'eur').toUpperCase() }).format(amount / 100)
    : '—';

const BillingSection: React.FC = () => {
  const token = localStorage.getItem('authToken');
  const [sub, setSub]             = useState<Subscription | null>(null);
  const [invoices, setInvoices]   = useState<Invoice[]>([]);
  const [loading, setLoading]     = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [resuming, setResuming]   = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [msg, setMsg]             = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [noCustomer, setNoCustomer] = useState(false);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [subRes, invRes] = await Promise.all([
        fetch('/api/billing/subscription', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/billing/invoices',     { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      // Une réponse en erreur ne doit PAS être confondue avec « aucune facture ».
      if (!subRes.ok || !invRes.ok) {
        const bad = !invRes.ok ? invRes : subRes;
        let detail = '';
        try {
          const body = await bad.json();
          detail = body.message || body.error || '';
        } catch { /* réponse non JSON */ }

        if (bad.status === 401) {
          throw new Error('Votre session a expiré. Reconnectez-vous pour voir vos factures.');
        }
        throw new Error(
          detail || `Le service de facturation est momentanément indisponible (erreur ${bad.status}).`
        );
      }

      const subData = await subRes.json();
      const invData = await invRes.json();

      setSub(subData.subscription ?? null);
      setInvoices(Array.isArray(invData.invoices) ? invData.invoices : []);
      setNoCustomer(invData.reason === 'NO_CUSTOMER');
    } catch (err: any) {
      setLoadError(err?.message || 'Impossible de charger vos informations de facturation.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCancel = async () => {
    setCancelling(true);
    setMsg(null);
    try {
      const res = await fetch('/api/billing/cancel', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setSub(prev => prev ? { ...prev, cancel_at_period_end: true } : prev);
      setMsg({ type: 'success', text: `Abonnement résilié. Vous conservez l'accès jusqu'au ${fmt(data.current_period_end)}.` });
      setConfirmCancel(false);
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setCancelling(false);
    }
  };

  const handleResume = async () => {
    setResuming(true);
    setMsg(null);
    try {
      const res = await fetch('/api/billing/resume', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setSub(prev => prev ? { ...prev, cancel_at_period_end: false } : prev);
      setMsg({ type: 'success', text: 'Résiliation annulée. Votre abonnement continue normalement.' });
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setResuming(false);
    }
  };

  const handlePortal = async () => {
    setPortalLoading(true);
    try {
      const res = await fetch('/api/billing/portal', { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      window.location.href = data.url;
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message });
      setPortalLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16 text-gray-400 text-sm">Chargement...</div>;
  }

  if (loadError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
        <p className="text-sm font-semibold text-red-800 mb-1">Vos factures n'ont pas pu être chargées</p>
        <p className="text-sm text-red-700 mb-4">{loadError}</p>
        <button
          onClick={load}
          className="px-4 py-2 text-sm font-semibold bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors"
        >
          Réessayer
        </button>
        <p className="text-xs text-red-500 mt-4">
          Si le problème persiste, contactez le support : le détail de votre abonnement reste consultable
          depuis le lien de facturation reçu par email.
        </p>
      </div>
    );
  }

  const isTrialing = sub?.status === 'trialing' || sub?.trial_end;
  const isCancelPending = sub?.cancel_at_period_end;

  return (
    <div className="space-y-6">

      {msg && (
        <div className={`px-4 py-3 rounded-xl text-sm ${msg.type === 'success' ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          {msg.text}
        </div>
      )}

      {/* ── Statut abonnement ── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Statut de l'abonnement</h3>

        {!sub ? (
          <p className="text-sm text-gray-500">Aucun abonnement actif.</p>
        ) : (
          <div className="space-y-4">
            {/* Badge statut */}
            <div className="flex items-center gap-3">
              {isCancelPending ? (
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700 border border-orange-200">
                  ⚠️ Résiliation en cours
                </span>
              ) : isTrialing ? (
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-violet-100 text-violet-700 border border-violet-200">
                  🎁 Période d'essai gratuite
                </span>
              ) : (
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">
                  ✅ Abonnement actif
                </span>
              )}
            </div>

            {/* Infos */}
            <div className="grid grid-cols-2 gap-4">
              {isTrialing && sub.trial_end && (
                <div className="bg-violet-50 rounded-xl p-3">
                  <p className="text-xs text-violet-500 font-medium">Fin de l'essai gratuit</p>
                  <p className="text-sm font-semibold text-violet-900 mt-0.5">{fmt(sub.trial_end)}</p>
                </div>
              )}
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-xs text-gray-500 font-medium">
                  {isCancelPending ? 'Accès jusqu\'au' : 'Prochaine facturation'}
                </p>
                <p className="text-sm font-semibold text-gray-900 mt-0.5">{fmt(sub.current_period_end)}</p>
              </div>
              {sub.amount && (
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500 font-medium">Montant</p>
                  <p className="text-sm font-semibold text-gray-900 mt-0.5">{fmtAmount(sub.amount, sub.currency)} / mois</p>
                </div>
              )}
            </div>

            {isCancelPending && (
              <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 text-sm text-orange-800">
                Votre abonnement sera résilié le <strong>{fmt(sub.current_period_end)}</strong>. Vous pouvez annuler cette résiliation à tout moment.
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-2 pt-1">
              <button
                onClick={handlePortal}
                disabled={portalLoading}
                className="px-4 py-2 text-sm font-medium bg-violet-600 text-white rounded-xl hover:bg-violet-700 disabled:opacity-60 transition-colors"
              >
                {portalLoading ? '...' : '💳 Mettre à jour ma carte'}
              </button>

              {isCancelPending ? (
                <button
                  onClick={handleResume}
                  disabled={resuming}
                  className="px-4 py-2 text-sm font-medium bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-60 transition-colors"
                >
                  {resuming ? '...' : '↩️ Annuler la résiliation'}
                </button>
              ) : (
                <button
                  onClick={() => setConfirmCancel(true)}
                  className="px-4 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-xl hover:bg-red-50 transition-colors"
                >
                  Résilier l'abonnement
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Confirmation résiliation ── */}
      {confirmCancel && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-5">
          <h4 className="font-semibold text-red-800 mb-1">Confirmer la résiliation</h4>
          <p className="text-sm text-red-700 mb-4">
            Vous conserverez l'accès jusqu'à la fin de la période en cours ({sub ? fmt(sub.current_period_end) : ''}). Aucun remboursement ne sera effectué.
          </p>
          <div className="flex gap-2">
            <button
              onClick={handleCancel}
              disabled={cancelling}
              className="px-4 py-2 text-sm font-semibold bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-60 transition-colors"
            >
              {cancelling ? 'Résiliation...' : 'Oui, résilier'}
            </button>
            <button
              onClick={() => setConfirmCancel(false)}
              className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* ── Factures ── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Factures</h3>

        {invoices.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-sm text-gray-400">
              {noCustomer
                ? "Aucun compte de facturation n'est encore rattaché à votre profil."
                : "Aucune facture pour le moment."}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              {noCustomer
                ? "C'est normal si vous n'avez pas encore souscrit. Sinon, contactez le support."
                : "Votre première facture apparaîtra à la fin de votre période d'essai."}
            </p>
            <button onClick={load} className="mt-3 text-xs font-medium text-violet-600 hover:text-violet-700 underline">
              Actualiser
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {invoices.map(inv => (
              <div key={inv.id} className="flex items-center justify-between py-3">
                <div>
                  <p className="text-sm font-medium text-gray-800">{inv.number || inv.id}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {fmt(inv.period_start)} → {fmt(inv.period_end)}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                    inv.status === 'paid'   ? 'bg-emerald-100 text-emerald-700'
                    : inv.status === 'open' ? 'bg-orange-100 text-orange-700'
                    : 'bg-gray-100 text-gray-600'
                  }`}>
                    {STATUS_LABELS[inv.status] || inv.status}
                  </span>
                  <span className="text-sm font-semibold text-gray-800">
                    {fmtAmount(inv.status === 'paid' ? inv.amount_paid : (inv.amount_due ?? inv.amount_paid), inv.currency)}
                  </span>
                  {inv.pdf ? (
                    <a
                      href={inv.pdf}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-violet-600 hover:text-violet-700 text-xs font-medium underline"
                    >
                      PDF
                    </a>
                  ) : (
                    <span className="text-xs text-gray-300">—</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingSection;
